import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  HideImageOutlined,
} from '@mui/icons-material';
import apiClient from '../utils/apiClient';
import { getImageUrl } from '../utils/imageUtils';
import { buildProductImageGallery } from '../utils/productImageGallery';

// Меняйте эти значения, чтобы визуально подобрать размер главного изображения.
const PRODUCT_IMAGE_SIZE = {
  width: '100%',
  maxWidth: { xs: 360, md: 560 },
  height: { xs: 300, md: 480 },
};

function ProductImageCarousel({
  product,
  glassTypeId,
  hardwareSetId,
  glassTypeName,
  hardwareSetName,
  photosEndpoint,
  onImageClick,
  imageSx,
}) {
  const [media, setMedia] = useState({ productId: null, renders: [], photos: [] });
  const [navigation, setNavigation] = useState({ key: '', index: 0 });

  useEffect(() => {
    if (!product?.id) return undefined;

    let active = true;
    const productId = product.id;

    const updateMedia = (field, values) => {
      if (!active) return;
      setMedia((current) => {
        const next = String(current.productId) === String(productId)
          ? current
          : { productId, renders: [], photos: [] };
        return { ...next, [field]: values };
      });
    };

    apiClient.get(`/public/products/${productId}/variant-renders`)
      .then(({ data }) => updateMedia('renders', data.renders || []))
      .catch(() => updateMedia('renders', []));

    apiClient.get(photosEndpoint)
      .then(({ data }) => updateMedia('photos', data.photos || []))
      .catch(() => updateMedia('photos', []));

    return () => {
      active = false;
    };
  }, [photosEndpoint, product?.id]);

  const productMedia = String(media.productId) === String(product?.id)
    ? media
    : { renders: [], photos: [] };

  const images = useMemo(
    () => buildProductImageGallery({
      defaultImageUrl: product?.img_url,
      variantRenders: productMedia.renders,
      photos: productMedia.photos,
      glassTypeId,
      hardwareSetId,
      glassTypeName,
      hardwareSetName,
    }),
    [
      glassTypeId,
      glassTypeName,
      hardwareSetId,
      hardwareSetName,
      product?.img_url,
      productMedia.photos,
      productMedia.renders,
    ],
  );

  const selectionKey = `${product?.id || ''}:${glassTypeId || ''}:${hardwareSetId || ''}`;
  const requestedIndex = navigation.key === selectionKey ? navigation.index : 0;
  const currentIndex = images.length ? Math.min(requestedIndex, images.length - 1) : 0;
  const currentImage = images[currentIndex] || null;
  const hasMultiple = images.length > 1;

  const navigate = (delta) => {
    if (!hasMultiple) return;
    setNavigation({
      key: selectionKey,
      index: (currentIndex + delta + images.length) % images.length,
    });
  };

  const imageAlt = currentImage?.caption || product?.name || 'Изображение изделия';
  const resolvedUrl = currentImage ? getImageUrl(currentImage.img_url) : null;

  return (
    <Box sx={{ my: 3, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {hasMultiple && (
          <IconButton
            aria-label="Предыдущее изображение"
            onClick={() => navigate(-1)}
            sx={{ '@media print': { display: 'none' } }}
          >
            <ArrowBackIosNew />
          </IconButton>
        )}

        {currentImage ? (
          <Box
            component="img"
            src={resolvedUrl}
            alt={imageAlt}
            onClick={onImageClick ? (event) => onImageClick(resolvedUrl, imageAlt, event) : undefined}
            sx={{
              ...PRODUCT_IMAGE_SIZE,
              objectFit: 'contain',
              borderRadius: 2,
              cursor: onImageClick ? 'pointer' : 'default',
              ...imageSx,
            }}
          />
        ) : (
          <Box
            role="img"
            aria-label="Нет изображения"
            sx={{
              width: '100%',
              maxWidth: 400,
              minHeight: 240,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              bgcolor: 'action.hover',
              borderRadius: 2,
            }}
          >
            <HideImageOutlined sx={{ fontSize: 64, mb: 1 }} />
            <Typography>Нет изображения</Typography>
          </Box>
        )}

        {hasMultiple && (
          <IconButton
            aria-label="Следующее изображение"
            onClick={() => navigate(1)}
            sx={{ '@media print': { display: 'none' } }}
          >
            <ArrowForwardIos />
          </IconButton>
        )}
      </Box>

      {hasMultiple && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1, '@media print': { display: 'none' } }}
        >
          {currentIndex + 1} из {images.length}
        </Typography>
      )}
    </Box>
  );
}

export default ProductImageCarousel;
